import { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const [isArabic, setIsArabic] = useState(i18n.language === 'ar');
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Update document direction and language attribute
  useEffect(() => {
    const lang = isArabic ? 'ar' : 'en';
    const dir = isArabic ? 'rtl' : 'ltr';
    
    document.documentElement.lang = lang;
    document.documentElement.dir = dir;
    document.documentElement.classList.toggle('rtl', isArabic);
    document.documentElement.classList.toggle('ltr', !isArabic);
  }, [isArabic]);

  const toggleLanguage = async () => {
    setIsTransitioning(true);
    
    // Small delay for smooth transition
    await new Promise(resolve => setTimeout(resolve, 150));
    
    const newLang = isArabic ? 'en' : 'ar';
    await i18n.changeLanguage(newLang);
    setIsArabic(!isArabic);
    
    // Allow transition to complete
    await new Promise(resolve => setTimeout(resolve, 150));
    setIsTransitioning(false);
  };

  const setLanguage = async (lang) => {
    if ((lang === 'ar') === isArabic) return;
    
    setIsTransitioning(true);
    await new Promise(resolve => setTimeout(resolve, 150));
    
    await i18n.changeLanguage(lang);
    setIsArabic(lang === 'ar');
    
    await new Promise(resolve => setTimeout(resolve, 150));
    setIsTransitioning(false);
  };

  return (
    <LanguageContext.Provider value={{ 
      isArabic, 
      isTransitioning, 
      toggleLanguage, 
      setLanguage,
      currentLanguage: isArabic ? 'ar' : 'en'
    }}>
      <div className={`language-transition ${isTransitioning ? 'transitioning' : ''}`}>
        {children}
      </div>
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
